import React from 'react';
import { ModalidadeAluguel, ModalidadeAluguelCompleta, MODALIDADES_OPCOES } from '../types/Property';

/**
 * Obtém o label amigável para uma modalidade de aluguel
 * @param modalidade - A modalidade de aluguel (enum ou string)
 * @returns O label amigável ou string vazia se não fornecida
 */
export const getModalidadeLabel = (modalidade?: ModalidadeAluguel | string): string => {
  if (!modalidade) return '';

  const labels: Record<ModalidadeAluguel, string> = {
    [ModalidadeAluguel.DEPOSITO_CAUCAO]: 'Depósito Caução',
    [ModalidadeAluguel.CARTA_FIANCA]: 'Carta Fiança',
    [ModalidadeAluguel.TITULO_CAPITALIZACAO]: 'Título de Capitalização',
    [ModalidadeAluguel.SEGURO_FIANCA]: 'Seguro Fiança',
    [ModalidadeAluguel.FIADOR]: 'Fiador',
  };

  return labels[modalidade as ModalidadeAluguel] || modalidade;
};

/**
 * Obtém o ícone (em string) para uma modalidade de aluguel (compatibilidade)
 * @param modalidade - A modalidade de aluguel (enum ou string)
 * @returns O ícone correspondente (string vazia pois agora usamos SVG)
 */
export const getModalidadeIcon = (modalidade?: ModalidadeAluguel | string): string => {
  // Mantido para compatibilidade, mas retorna string vazia
  // Agora usamos getModalidadeIconSvg para componentes SVG
  return '';
};

/**
 * Obtém o componente SVG do ícone para uma modalidade de aluguel
 * @param modalidade - A modalidade de aluguel (enum ou string)
 * @param size - Tamanho do ícone (padrão: 18)
 * @returns Componente SVG React
 */
export const getModalidadeIconSvg = (modalidade?: ModalidadeAluguel | string, size: number = 18): React.ReactElement | null => {
  if (!modalidade) return null;

  const modalidadeStr = modalidade.toString();
  
  // Depósito Caução - ícone de dinheiro/moeda
  if (modalidadeStr === ModalidadeAluguel.DEPOSITO_CAUCAO || modalidadeStr === 'deposito_caucao') {
    return (
      <svg className="modalidade-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
        <path d="M12 6V18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <path d="M8 12H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="12" cy="12" r="2" fill="currentColor"/>
      </svg>
    );
  }
  
  // Carta Fiança - ícone de documento
  if (modalidadeStr === ModalidadeAluguel.CARTA_FIANCA || modalidadeStr === 'carta_fianca') {
    return (
      <svg className="modalidade-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M7 12H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <path d="M7 16H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    );
  }
  
  // Título de Capitalização - ícone de gráfico/crescimento
  if (modalidadeStr === ModalidadeAluguel.TITULO_CAPITALIZACAO || modalidadeStr === 'titulo_capitalizacao') {
    return (
      <svg className="modalidade-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 20L9 14L13 18L21 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M21 10V4H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="3" cy="20" r="2" fill="currentColor"/>
        <circle cx="9" cy="14" r="2" fill="currentColor"/>
        <circle cx="13" cy="18" r="2" fill="currentColor"/>
        <circle cx="21" cy="10" r="2" fill="currentColor"/>
      </svg>
    );
  }
  
  // Seguro Fiança - ícone de escudo/proteção
  if (modalidadeStr === ModalidadeAluguel.SEGURO_FIANCA || modalidadeStr === 'seguro_fianca') {
    return (
      <svg className="modalidade-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L3 6L3 11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11L21 6L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }
  
  // Fiador - ícone de usuário/pessoa
  if (modalidadeStr === ModalidadeAluguel.FIADOR || modalidadeStr === 'fiador') {
    return (
      <svg className="modalidade-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="8" r="5" stroke="currentColor" strokeWidth="2"/>
        <path d="M3 21C3 16.0294 7.02944 12 12 12C16.9706 12 21 16.0294 21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    );
  }

  return null;
};

/**
 * Obtém labels de um array de modalidades
 * @param modalidades - Array de modalidades completas
 * @returns Array de labels
 */
export const getModalidadesLabels = (modalidades?: ModalidadeAluguelCompleta[]): string[] => {
  if (!modalidades || modalidades.length === 0) return [];
  return modalidades.map(m => m.nome || getModalidadeLabel(m.tipo));
};

/**
 * Extrai tipos de modalidade de um array completo para enviar à API
 * @param modalidades - Array de modalidades completas
 * @returns Array de tipos de modalidade (strings)
 */
export const extractModalidadesTypes = (modalidades?: ModalidadeAluguelCompleta[]): string[] => {
  if (!modalidades || modalidades.length === 0) return [];
  return modalidades.map(m => m.tipo);
};

/**
 * Converte tipo de modalidade único (para compatibilidade) para array
 * @param modalidade - Modalidade única (campo antigo)
 * @returns Array com um único tipo
 */
export const convertModalidadeToArray = (modalidade?: ModalidadeAluguel | string): string[] => {
  if (!modalidade) return [];
  return [modalidade.toString()];
};

/**
 * Obtém modalidades ativas do imóvel (usa novo formato ou compatibilidade)
 * @param imovel - Objeto imóvel
 * @returns Array de modalidades completas ou array vazio
 */
export const getImovelModalidades = (imovel?: {
  modalidades?: ModalidadeAluguelCompleta[];
  modalidade_aluguel?: ModalidadeAluguel | string;
}): ModalidadeAluguelCompleta[] => {
  // Priorizar novo formato
  if (imovel?.modalidades && imovel.modalidades.length > 0) {
    return imovel.modalidades;
  }
  
  // Fallback para formato antigo (compatibilidade)
  if (imovel?.modalidade_aluguel) {
    const tipo = imovel.modalidade_aluguel.toString();
    // Tentar encontrar opção comparando como string ou enum
    const opcao = MODALIDADES_OPCOES.find(m => 
      m.value.toString() === tipo || 
      m.value === tipo ||
      (typeof imovel.modalidade_aluguel !== 'string' && m.value === imovel.modalidade_aluguel)
    );
    if (opcao) {
      return [{
        id: '',
        tipo: opcao.value.toString(),
        nome: opcao.label,
        descricao: opcao.description,
        created_at: '',
        updated_at: ''
      }];
    }
  }
  
  return [];
};

/**
 * Valida se os tipos de modalidade são válidos
 */
export function validateModalidades(modalidades: string[]): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  const validTypes = MODALIDADES_OPCOES.map(m => m.value.toString());

  modalidades.forEach((tipo, index) => {
    if (!validTypes.includes(tipo)) {
      errors.push(`Modalidade inválida na posição ${index + 1}: ${tipo}`);
    }
  });

  // Verificar duplicatas
  const duplicates = modalidades.filter((item, index) => 
    modalidades.indexOf(item) !== index
  );
  
  if (duplicates.length > 0) {
    errors.push(`Modalidades duplicadas encontradas: ${duplicates.join(', ')}`);
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Sanitiza array de modalidades
 */
export function sanitizeModalidades(modalidades: string[]): string[] {
  const validTypes = MODALIDADES_OPCOES.map(m => m.value.toString());
  
  return modalidades
    .filter((tipo, index, self) => {
      // Filtrar tipos inválidos
      if (!validTypes.includes(tipo)) {
        console.warn(`Tipo de modalidade inválido ignorado: ${tipo}`);
        return false;
      }
      // Remover duplicatas
      return self.indexOf(tipo) === index;
    });
}
